import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck,
  CheckCircle2,
  Loader2,
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  Clock,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paid, setPaid] = useState(false);

  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled") === "true";

  const fetchBooking = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(res.data);
      if (res.data.deposit_status === "paid") setPaid(true);
    } catch (e) {
      toast.error("Booking not found.");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  // Poll status when returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let cancelledFlag = false;
    setPolling(true);
    const tick = async () => {
      if (cancelledFlag) return;
      if (attempts >= 8) {
        setPolling(false);
        toast.message("Still processing — refresh in a moment.");
        return;
      }
      attempts += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setPaid(true);
          setPolling(false);
          toast.success("Payment received — your spot is locked in!");
          await fetchBooking();
          // Clean URL
          setParams({}, { replace: true });
          return;
        }
        if (res.data.status === "expired") {
          setPolling(false);
          toast.error("Payment session expired. Please retry.");
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        setPolling(false);
        toast.error("Could not verify payment.");
      }
    };
    tick();
    return () => {
      cancelledFlag = true;
    };
  }, [sessionId, fetchBooking, setParams]);

  useEffect(() => {
    if (cancelled) toast.message("Payment cancelled. You can retry below.");
  }, [cancelled]);

  const startCheckout = async () => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/bookings/${bookingId}/checkout`,
        { origin_url: window.location.origin }
      );
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not start checkout. Please try again."
      );
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0A0A0C] text-white/60">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0A0A0C] text-white px-6">
        <div className="text-center">
          <AlertCircle className="mx-auto text-[#D4AF37] mb-4" size={36} />
          <h1 className="text-2xl font-semibold mb-2">Booking not found</h1>
          <p className="text-white/55 mb-6">The link may have expired.</p>
          <Link to="/">
            <Button className="bg-[#D4AF37] text-black hover:bg-[#C5A017] rounded-full">
              Back to home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white">
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-[#D4AF37] mb-10"
        >
          <ArrowLeft size={15} /> Back to All Service Proz
        </Link>

        <div className="flex items-center gap-2 mb-3">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#D4AF37] text-black">
            <ShieldCheck size={18} strokeWidth={2.2} />
          </span>
          <span className="font-semibold tracking-tight text-white text-lg">
            All Service <span className="text-[#D4AF37]">Proz</span>
          </span>
        </div>

        <p className="text-xs uppercase tracking-[0.28em] text-[#D4AF37] font-semibold mb-3">
          Secure Your Booking
        </p>
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tighter mb-3">
          {paid ? "Deposit received." : "Pay your deposit."}
        </h1>
        <p className="text-white/65 text-base md:text-lg leading-relaxed max-w-xl">
          {paid
            ? "Thank you — your appointment is locked in. We'll see you on site."
            : `A refundable $${Number(booking.deposit_amount).toFixed(0)} deposit secures your appointment slot. It's applied to your final invoice.`}
        </p>

        {/* Booking summary card */}
        <div
          data-testid="booking-summary"
          className="mt-10 rounded-2xl bg-[#121216] border border-white/10 overflow-hidden"
        >
          <div className="p-6 md:p-8 border-b border-white/10">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
              Service
            </p>
            <h2 className="text-xl md:text-2xl font-semibold mt-1">
              {booking.service_title}
            </h2>
            <p className="text-sm text-white/55 mt-1">
              Starting at ${booking.starting_price} · final quote on-site
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-px bg-white/5">
            <Cell icon={CalendarDays} label="Date" value={booking.booking_date} />
            <Cell icon={Clock} label="Time" value={booking.booking_time} />
            <Cell icon={MapPin} label="Address" value={booking.address} clamp />
          </div>

          <div className="p-6 md:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/45 font-semibold">
                Deposit due
              </p>
              <p className="text-3xl font-semibold text-[#D4AF37] mt-1">
                ${Number(booking.deposit_amount).toFixed(2)}
              </p>
              <p className="text-xs text-white/45 mt-1">Refundable up to 24h prior</p>
            </div>

            {paid ? (
              <div
                data-testid="payment-success-badge"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] font-semibold"
              >
                <CheckCircle2 size={18} /> Deposit Paid
              </div>
            ) : polling ? (
              <Button
                data-testid="payment-polling"
                disabled
                className="bg-white/10 text-white rounded-full h-12 px-7 min-w-[200px]"
              >
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying payment…
              </Button>
            ) : (
              <Button
                data-testid="pay-deposit-button"
                onClick={startCheckout}
                disabled={creating}
                className="bg-[#D4AF37] text-black hover:bg-[#C5A017] font-semibold rounded-full h-12 px-7 min-w-[200px]"
              >
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting…
                  </>
                ) : (
                  `Pay $${Number(booking.deposit_amount).toFixed(0)} Deposit`
                )}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-white/55">
          <Trust label="Stripe-secured" />
          <Trust label="256-bit SSL" />
          <Trust label="Refundable" />
          <Trust label="No hidden fees" />
        </div>

        <p className="mt-8 text-sm text-white/45">
          Questions? Call us at{" "}
          <a href="tel:4708080832" className="text-[#D4AF37] hover:underline font-semibold">
            470-808-0832
          </a>
        </p>
      </div>
    </div>
  );
}

function Cell({ icon: Icon, label, value, clamp }) {
  return (
    <div className="bg-[#121216] p-5">
      <Icon className="text-[#D4AF37] mb-2" size={16} strokeWidth={1.7} />
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 font-semibold mb-1">
        {label}
      </div>
      <div className={`text-sm text-white ${clamp ? "line-clamp-2" : ""}`}>{value}</div>
    </div>
  );
}

function Trust({ label }) {
  return (
    <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-center">
      {label}
    </div>
  );
}
